import { KitIcon } from '../types/KitIcon';
import { SceneGuid } from '../types/Scene';
import { SceneLink, SceneLinkGuid } from '../types/SceneLink';
import { Position, TourScenePanoramaDto } from '@ael/core-api';

export type SceneAddedEvent = {
  guid: SceneGuid;
  renderSettings: TourScenePanoramaDto;
};

export type SceneReplacedEvent = {
  guid: SceneGuid;
  renderSettings: TourScenePanoramaDto;
};

export type SceneDeletedEvent = {
  guid: SceneGuid;
};

export type LinkBetweenScenesAddedEvent = {
  guid: SceneLinkGuid;
  sceneLink: SceneLink;
};

export type LinkChangedDestinationSceneEvent = {
  guid: SceneLinkGuid;
  newSceneGuid: SceneGuid;
};

export type LinkChangedDestinationLabelEvent = {
  guid: SceneLinkGuid;
  newLabel?: string;
};

export type LinkCoordinatesChangedEvent = {
  guid: SceneLinkGuid;
  position: Position;
};

export type LinkIconChangedEvent = {
  guid: SceneLinkGuid;
  icon: KitIcon;
  hasTilt: boolean;
};

export type LinkDeletedEvent = {
  guid: SceneLinkGuid;
};

/***
 * Encapsulates events that in tour builder store.
 */
export class TourBuilderStoreEvents {
  public readonly onSceneAdded = new EventHandlerCollection<SceneAddedEvent>();
  public readonly onSceneReplaced = new EventHandlerCollection<SceneReplacedEvent>();
  public readonly onSceneDeleted = new EventHandlerCollection<SceneDeletedEvent>();

  public readonly onLinkAdded = new EventHandlerCollection<LinkBetweenScenesAddedEvent>();
  public readonly onLinkDestinationChanged = new EventHandlerCollection<LinkChangedDestinationSceneEvent>();
  public readonly onLinkDestinationLabelChanged = new EventHandlerCollection<LinkChangedDestinationLabelEvent>();
  public readonly onLinkCoordinatesChanged = new EventHandlerCollection<LinkCoordinatesChangedEvent>();
  public readonly onLinkIconChanged = new EventHandlerCollection<LinkIconChangedEvent>();
  public readonly onLinkDeleted = new EventHandlerCollection<LinkDeletedEvent>();
}

type EventHandler<TEvent> = (event: TEvent) => void;

/***
 * Represents a collection of event handlers subscribed to an occurrence of the event.
 */
export class EventHandlerCollection<TEvent> {
  private readonly _eventHandlers: Set<EventHandler<TEvent>> = new Set<EventHandler<TEvent>>();

  /***
   * Subscribes the event handler to an event.
   * @returns Function to unsubscribe the handler.
   */
  subscribe = (eventHandler: EventHandler<TEvent>): (() => void) => {
    this._eventHandlers.add(eventHandler);
    return () => this._eventHandlers.delete(eventHandler);
  };

  /***
   * Notifies subscribed handlers about the event.
   */
  notify = (event: TEvent) => {
    this._eventHandlers.forEach((handler) => {
      handler(event);
    });
  };
}
